import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Phone, FileWarning, AlertTriangle, ChevronRight, MessagesSquare, Sparkles, Footprints, FileText } from "lucide-react";
import OneToOneModal from "@/components/OneToOneModal";
import { scadutoLabel, giorniScaduto } from "@/pages/Task";
const localISO = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

export default function MiaGiornata() {
  const { user } = useAuth();
  const [o2o, setO2o] = useState(false);
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const isBuyer = user?.role === "buyer";

  const [scad, setScad] = useState([]);
  const load = () => {
    api.get("/attivita").then((r) => setScad(r.data.filter((t) => t.stato !== "completato" && t.scadenza && t.scadenza < localISO(new Date())).sort((a, b) => a.scadenza.localeCompare(b.scadenza)))).catch(() => {});
    return api.get("/dashboard/mia-giornata").then((r) => setD(r.data)).catch(() => setD({}));
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const oggi = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" });
  if (!d) return <div className="animate-pulse h-64 rounded-2xl bg-slate-100" />;

  const appts = d.appuntamenti_oggi || [];
  const rich = d.da_richiamare || [];
  const docs = d.documenti_mancanti || [];

  return (
    <div className="fade-up w-full max-w-full overflow-x-hidden" data-testid="mia-giornata">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[var(--muted)] mb-1">{oggi}</div>
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">La mia giornata</h1>
        <p className="text-sm text-[var(--muted)] mt-1">Ciao {user?.name?.split(" ")[0]}, ecco cosa devi fare oggi.</p>
        <button data-testid="mg-o2o-btn" onClick={() => setO2o(true)} className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-xs font-semibold hover:border-[#FC82ED]"><MessagesSquare size={14} className="text-[#FC82ED]" /> Il mio one-to-one</button>
      </div>
      {o2o && <OneToOneModal agentId={user.id} onClose={() => setO2o(false)} />}

      {d.esiti_mancanti > 0 && (
        <button data-testid="mg-esiti-mancanti" onClick={() => nav("/appuntamenti-oggi")}
          className="w-full mb-5 flex items-center gap-3 p-4 rounded-2xl border-2 border-red-300 bg-red-50 text-left hover:-translate-y-0.5 transition-transform">
          <AlertTriangle className="text-red-500 shrink-0" size={20} />
          <div className="text-sm text-red-700 flex-1"><b>{d.esiti_mancanti}</b> {d.esiti_mancanti === 1 ? "appuntamento finito senza esito" : "appuntamenti finiti senza esito"}. Registra com'è andata.</div>
          <ChevronRight className="text-red-400" size={18} />
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        {scad.length > 0 && (
          <Section icon={AlertTriangle} title="Task scaduti" count={scad.length} testid="mg-task-scaduti" onAll={() => nav("/task")}>
            {scad.slice(0, 6).map((t) => (
              <Rowline key={t.id} testid={`mg-task-scaduto-${t.id}`} onClick={() => nav("/task")}
                left={<span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold whitespace-nowrap">{giorniScaduto(t.scadenza)} gg</span>}
                title={t.titolo} sub={scadutoLabel(t.scadenza)} />
            ))}
          </Section>
        )}
        <Section icon={CalendarClock} title="Appuntamenti di oggi" count={appts.length} testid="mg-appuntamenti" onAll={() => nav("/appuntamenti-oggi")}>
          {appts.length === 0 ? <Empty>Nessun appuntamento oggi.</Empty> : appts.map((a) => (
            <Rowline key={a.id} testid={`mg-appt-${a.id}`} onClick={() => nav("/appuntamenti-oggi")}
              left={<span className="font-mono text-xs text-[#FC82ED] font-bold">{fmtTime ? fmtTime(a.data) : (a.data || "").slice(11, 16)}</span>}
              title={a.titolo || (a.tipo || "Appuntamento")} sub={a.tipo} />
          ))}
        </Section>

        <Section icon={Phone} title={isBuyer ? "Acquirenti da richiamare" : "Da richiamare"} count={rich.length} testid="mg-richiamare">
          {rich.length === 0 ? <Empty>Nessuno da richiamare.</Empty> : rich.map((r) => (
            <Rowline key={`${r.tipo}-${r.id}`} testid={`mg-rec-${r.id}`} onClick={() => nav(r.link)}
              left={<span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${r.tipo === "notizia" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>{r.tipo === "notizia" ? "Notizia" : "Acquirente"}</span>}
              title={r.nome} sub={[r.da_completare ? "dati proprietario da completare" : "", r.quando ? `entro il ${r.quando}` : ""].filter(Boolean).join(" · ")} />
          ))}
        </Section>

        {isBuyer && <BuyerSections d={d} nav={nav} />}

        <Section icon={FileWarning} title="Documenti mancanti" count={docs.length} testid="mg-documenti">
          {docs.length === 0 ? <Empty>Tutte le pratiche in regola.</Empty> : docs.map((im) => (
            <Rowline key={im.id} testid={`mg-doc-${im.id}`} onClick={() => nav(im.link)}
              left={<span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{im.mancanti}</span>}
              title={im.titolo} sub={`${im.mancanti} ${im.mancanti === 1 ? "documento" : "documenti"} da caricare`} />
          ))}
        </Section>

      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, count, children, onAll, testid }) => (
  <div className="card-immo p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <span className="w-8 h-8 rounded-xl bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center"><Icon size={16} /></span>
      <h2 className="font-display font-bold text-base flex-1">{title}</h2>
      {typeof count === "number" && <span className="text-xs font-bold text-[var(--muted)] bg-slate-100 rounded-full px-2 py-0.5">{count}</span>}
    </div>
    <div className="space-y-1.5">{children}</div>
    {onAll && <button onClick={onAll} className="mt-3 text-xs font-semibold text-[#FC82ED] hover:underline">Vedi tutti →</button>}
  </div>
);

const Rowline = ({ left, title, sub, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 text-left transition-colors">
    <div className="shrink-0 w-14 flex justify-start">{left}</div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium truncate">{title}</div>
      {sub && <div className="text-xs text-[var(--muted)] truncate">{sub}</div>}
    </div>
    <ChevronRight className="text-slate-300 shrink-0" size={16} />
  </button>
);

const Empty = ({ children }) => <p className="text-sm text-[var(--muted)] py-2">{children}</p>;

function BuyerSections({ d, nav }) {
  const nuovi = d.buyer_nuovi_compatibili || [];
  const visite = d.buyer_visite_settimana || [];
  const prop = d.buyer_proposte || [];
  return (
    <>
      <Section icon={Sparkles} title="Immobili nuovi compatibili (14 giorni)" count={nuovi.length} testid="mg-buyer-nuovi">
        {nuovi.length === 0 ? <Empty>Nessun immobile nuovo compatibile.</Empty> : nuovi.map((x) => (
          <Rowline key={`${x.immobile_id}-${x.acquirente}`} testid={`mg-nuovo-${x.immobile_id}`} onClick={() => nav(x.link)}
            left={<span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{x.match}%</span>}
            title={x.titolo} sub={`per ${x.acquirente}`} />
        ))}
      </Section>
      <Section icon={Footprints} title="Visite della settimana" count={visite.length} testid="mg-buyer-visite">
        {visite.length === 0 ? <Empty>Nessuna visita questa settimana.</Empty> : visite.map((v) => (
          <Rowline key={v.id} testid={`mg-visita-${v.id}`} onClick={() => nav("/agenda")}
            left={<span className="font-mono text-[11px] text-[#FC82ED] font-bold">{(v.data || "").slice(8, 10)}/{(v.data || "").slice(5, 7)}</span>}
            title={v.titolo} sub={(v.data || "").slice(11, 16)} />
        ))}
      </Section>
      <Section icon={FileText} title="Proposte in corso" count={prop.length} testid="mg-buyer-proposte">
        {prop.length === 0 ? <Empty>Nessuna proposta in corso.</Empty> : prop.map((p) => (
          <Rowline key={p.id} testid={`mg-proposta-${p.id}`} onClick={() => nav("/proposte")}
            left={<span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold">{p.stato}</span>}
            title={p.titolo} sub={p.importo ? `${Number(p.importo).toLocaleString("it-IT")} €` : ""} />
        ))}
      </Section>
    </>
  );
}
